import { useCallback, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { usePlayer } from '../hooks/usePlayer.js'
import { useVideoSync } from '../hooks/useVideoSync.js'
import { langFlag } from '../lib/languageFlags.js'
import { fetchCaptions } from '../lib/api.js'
import {
  type CaptionStyle,
  CAPTION_STYLES,
  STYLE_CONFIG,
  KaraokeCaptions,
  PlainCaption,
} from './Captions.js'

function formatTime(s: number): string {
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${m}:${sec.toString().padStart(2, '0')}`
}

const SPEAKER_COLORS = ['#FF006E', '#00C48C', '#3A0CA3', '#FFBE0B', '#4CC9F0']

export default function FullScreenPlayer() {
  const player = usePlayer()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [downloading, setDownloading] = useState(false)
  const [showCaptions, setShowCaptions] = useState(true)
  const [captionStyle, setCaptionStyle] = useState<CaptionStyle>('yellow')

  const meta = player.jobMeta

  // Video sync — active only when full-screen is open
  const { videoSrc, videoRef, hasVideo, isYouTube, ytReady } = useVideoSync({
    player,
    containerId: 'fs-yt-player',
    enabled: player.fullScreenOpen,
  })

  // Captions
  const { data: captionsData } = useQuery({
    queryKey: ['captions', player.jobId],
    queryFn: () => fetchCaptions(player.jobId!),
    enabled: !!player.jobId && player.fullScreenOpen,
    staleTime: Infinity,
  })
  const captions = captionsData?.captions
  const allWords = captions?.flatMap(c => c.words ?? []) ?? []
  const hasKaraoke = allWords.length > 0
  const activeSegment = !hasKaraoke
    ? captions?.find(c => player.currentTime >= c.startSeconds && player.currentTime < c.endSeconds)
    : undefined

  // ESC key dismiss
  useEffect(() => {
    if (!player.fullScreenOpen) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') player.closePlayers() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [player.fullScreenOpen, player.closePlayers])

  const handleDownload = useCallback(async () => {
    if (!player.audioUrl) return
    setDownloading(true)
    try {
      const res = await fetch(player.audioUrl)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${meta?.videoTitle ?? t('shell.dubbed')}.mp3`
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setDownloading(false)
    }
  }, [player.audioUrl, meta?.videoTitle])

  const handleShare = useCallback(async () => {
    if (!player.audioUrl) return
    if (navigator.share) {
      try {
        await navigator.share({ title: meta?.videoTitle ?? t('shell.dubbed'), url: player.audioUrl })
      } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(player.audioUrl)
    }
  }, [player.audioUrl, meta?.videoTitle])

  const handleRedub = useCallback(() => {
    player.closePlayers()
    navigate('/dashboard', {
      state: {
        prefill: {
          url: meta?.videoUrl ?? '',
          sourceLang: meta?.sourceLang ?? 'en',
          targetLang: meta?.targetLang ?? 'ko',
          originalVol: 15,
          dubbedVol: 100,
        },
      },
    })
  }, [player, navigate, meta])

  const handleBgClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      player.closePlayers()
    }
  }

  const progress = player.duration > 0 ? (player.currentTime / player.duration) * 100 : 0

  const variant = player.jobId ? `v${(parseInt(player.jobId.slice(-2), 16) % 6) + 1}` : 'v1'
  const langName = meta ? t(`languages.${meta.targetLang}`, meta.targetLang.toUpperCase()) : ''
  const title = meta?.videoTitle ?? t('shell.audioPlayer', 'Audio Player')

  return (
    <div className={`fs-modal${player.fullScreenOpen ? ' open' : ''}`} onClick={handleBgClick}>
      <div className="fs-backdrop" />
      <div className="fs-art-bg" style={{ background: 'radial-gradient(ellipse at 25% 35%,rgba(255,0,110,.5) 0%,transparent 60%),radial-gradient(ellipse at 75% 65%,rgba(58,12,163,.5) 0%,transparent 60%)' }} />
      <div className="fs-inner">
        <div className="fs-card">
          <button className="fs-close" onClick={() => player.closePlayers()}>
            <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>

          {/* Video / Thumbnail — large */}
          <div className="fs-thumb">
            {isYouTube && (
              <>
                <div id="fs-yt-player" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
                {!ytReady && <div className="skeleton-shimmer" style={{ position: 'absolute', inset: 0 }} />}
              </>
            )}
            {!isYouTube && videoSrc && (
              <video
                ref={videoRef}
                src={videoSrc}
                muted
                playsInline
                preload="metadata"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )}
            {!hasVideo && (
              meta?.videoThumbnailUrl?.startsWith('http') ? (
                <img src={meta.videoThumbnailUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <div className={`dub-thumb-bg ${variant}`} style={{ width: '100%', height: '100%' }} />
              )
            )}
            {showCaptions && (
              <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 2 }}>
                {hasKaraoke
                  ? <KaraokeCaptions words={allWords} currentTime={player.currentTime} style={captionStyle} wordsPerLine={6} />
                  : activeSegment
                    ? <PlainCaption text={activeSegment.text} style={captionStyle} />
                    : null}
              </div>
            )}
            <div className="fs-thumb-overlay" />
          </div>

          {/* Compact info row: badge + title + CC controls */}
          <div className="fs-info-compact">
            <div className="fs-info-left">
              <div className="fs-badge">
                <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
                {langName ? `${langFlag(meta!.targetLang)} ${langName}` : t('shell.dubbed', 'Dubbed')}
              </div>
              <div className="fs-title-compact">{title}</div>
            </div>
            {captions && captions.length > 0 && (
              <div className="fs-cc-row">
                <button
                  onClick={() => setShowCaptions(v => !v)}
                  className="fs-cc-btn"
                  style={{
                    background: showCaptions ? 'var(--cta)' : 'rgba(255,255,255,.1)',
                    color: showCaptions ? '#fff' : 'rgba(255,255,255,.4)',
                  }}
                >CC</button>
                {showCaptions && CAPTION_STYLES.map(s => (
                  <button
                    key={s}
                    onClick={() => setCaptionStyle(s)}
                    aria-label={s}
                    style={{
                      width: 12, height: 12, borderRadius: '50%', cursor: 'pointer', padding: 0,
                      border: captionStyle === s ? '2px solid var(--cta)' : '1.5px solid rgba(128,128,128,0.4)',
                      background: STYLE_CONFIG[s].swatch,
                      transform: captionStyle === s ? 'scale(1.2)' : 'scale(1)',
                      transition: 'transform .1s',
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Speaker chips */}
          {meta?.speakerProfiles && meta.speakerProfiles.length > 0 && (
            <div className="fs-speakers-compact">
              {meta.speakerProfiles.map((sp, i) => (
                <div key={i} className="speaker-chip">
                  <div className="sc-dot" style={{ background: SPEAKER_COLORS[i % SPEAKER_COLORS.length] }} />
                  <span className="sc-name">{sp.label}</span>
                  <span className="sc-lang">{sp.gender === 'male' ? 'M' : 'F'}</span>
                </div>
              ))}
            </div>
          )}

          {/* Seek slider */}
          <div className="fs-seek">
            <input
              type="range"
              className="fs-seek-slider"
              min="0"
              max="1000"
              value={Math.round(progress * 10)}
              style={{ '--val': `${progress}%` } as React.CSSProperties}
              onChange={e => player.seek((Number(e.target.value) / 1000) * player.duration)}
            />
            <div className="fs-seek-time">
              <span>{formatTime(player.currentTime)}</span>
              <span>{formatTime(player.duration)}</span>
            </div>
          </div>

          {/* Play controls centered + volume right */}
          <div className="fs-transport">
            <div className="fs-transport-center">
              <button className="fs-ctrl-btn" onClick={() => player.skip(-15)}>
                <svg viewBox="0 0 24 24">
                  <polyline points="1 4 1 10 7 10" />
                  <path d="M3.51 15a9 9 0 100-.49" />
                  <text x="10.5" y="16" fontFamily="sans-serif" fontSize="6" fill="currentColor" stroke="none">15</text>
                </svg>
              </button>
              <button className="fs-play-btn" onClick={player.togglePlay}>
                {player.playing ? (
                  <svg viewBox="0 0 24 24" width="20" height="20"><rect x="6" y="4" width="4" height="16" fill="white" /><rect x="14" y="4" width="4" height="16" fill="white" /></svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="20" height="20"><polygon points="8 5 19 12 8 19" fill="white" /></svg>
                )}
              </button>
              <button className="fs-ctrl-btn" onClick={() => player.skip(15)}>
                <svg viewBox="0 0 24 24">
                  <path d="M23 4v6h-6" />
                  <path d="M20.49 15a9 9 0 100 .49" />
                  <text x="4.5" y="16" fontFamily="sans-serif" fontSize="6" fill="currentColor" stroke="none">15</text>
                </svg>
              </button>
            </div>
            <div className="fs-vol-inline">
              <svg viewBox="0 0 24 24"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M15.54 8.46a5 5 0 010 7.07" /></svg>
              <input
                type="range"
                className="fs-vol-slider"
                min="0"
                max="100"
                value={Math.round(player.volume * 100)}
                style={{ '--val': `${Math.round(player.volume * 100)}%` } as React.CSSProperties}
                onChange={e => player.setVolume(Number(e.target.value) / 100)}
              />
            </div>
          </div>

          {/* Action strip */}
          <div className="fs-actions">
            {meta?.videoUrl && (
              <button className="fs-act yt" onClick={() => window.open(meta.videoUrl, '_blank')}>
                <svg viewBox="0 0 24 24"><path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 001.46 6.42 29 29 0 001 12a29 29 0 00.46 5.58 2.78 2.78 0 001.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.96A29 29 0 0023 12a29 29 0 00-.46-5.58z" /><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="currentColor" stroke="none" /></svg>
                YouTube
              </button>
            )}
            <button className="fs-act" onClick={() => void handleDownload()} disabled={downloading || !player.audioUrl}>
              <svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
              {downloading ? '...' : t('shell.download', 'Download')}
            </button>
            <button className="fs-act" onClick={() => void handleShare()} disabled={!player.audioUrl}>
              <svg viewBox="0 0 24 24"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" /></svg>
              {t('shell.share', 'Share')}
            </button>
            <button className="fs-act" onClick={handleRedub}>
              <svg viewBox="0 0 24 24"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 100-.49" /></svg>
              {t('shell.redubShort', 'Re-dub')}
            </button>
          </div>
        </div>
        <div style={{ marginTop: 12, fontSize: 11, color: 'rgba(255,255,255,.2)', textAlign: 'center' }}>
          {t('shell.playerHint', 'Playing dubbed audio only — open on YouTube to watch the video with this dub running alongside')}
        </div>
      </div>
    </div>
  )
}
